import React from 'react'
import styled from 'styled-components'
// @ts-ignore
import { Avatar, Skeleton } from '@netbasequid/core'
// @ts-ignore
import { SimpleBar, Sparkline } from '@netbasequid/chart'
import { ColDefs } from 'components/visualization/Table'
import { TopicComparisonMeasureType, MessageConst } from 'enums'
import i18n from 'i18n'

const AVATAR_DIAMETER = '32px'

const getFormattedValue = (cellValue: any) => cellValue?.formattedValue || ''

const SparklineContainer = styled.div`
  width: ${({ maxWidth }: { maxWidth: number }) => `min(100%, ${maxWidth})`};
  min-width: 100px;
  height: 32px;
`

// give a min height to topic name text to keep row height to 48px,
// or it will reduce to 42px when there's no avatar in the Insight column.
const TopicNameContainer = styled.div`
  min-height: 32px;
  display: flex;
  align-items: center;
`

const colDefs: ColDefs<any, any, any> = [
  {
    field: 'topicName',
    customHeaderRender: () => <>{i18n.t('insight')}</>,
    customCellRender: (cellValue, { row, constraints, theme }) => (
      <>
        {constraints.isDisplayIcon && (
          <Avatar
            mode={theme}
            src={row.imageUrl}
            diameter={AVATAR_DIAMETER}
            loader={
              <Skeleton
                variation="circle"
                width={AVATAR_DIAMETER}
                height={AVATAR_DIAMETER}
                animation="wave"
              />
            }
            alt={row.topicName}
          />
        )}
        <TopicNameContainer>
          <span>{cellValue}</span>
        </TopicNameContainer>
      </>
    ),
    cellProps: {
      textAlign: 'left',
      width: (_, { constraints }) => constraints.columnWidth
    },
    customFooterRender: () => i18n.t('summary')
  },
  {
    field: TopicComparisonMeasureType.MENTIONS,
    customHeaderRender: () => <>{i18n.t('mention_other')}</>,
    children: [
      {
        customCellRender: getFormattedValue,
        cellProps: {
          colSidePaddings: { right: '8px' },
          textAlign: 'right',
          fitWidth: true
        }
      },
      {
        customCellRender: (_, { row, theme }) => (
          <SimpleBar
            progress={row[TopicComparisonMeasureType.MENTIONS_PERCENT_SHARE].value * 100}
            colorSetting="metric.mainMetrics.mentions"
            mode={theme}
          />
        ),
        cellProps: {
          colSidePaddings: { left: '0px' },
          width: (_, { constraints }) => constraints.columnWidth
        }
      }
    ],
    customFooterRender: (context) => {
      const { statistics } = context
      return (
        <>
          <span>{statistics.totalMentions}</span>
          <span>({i18n.t('total')})</span>
        </>
      )
    }
  },
  {
    field: TopicComparisonMeasureType.MENTIONS_PERCENT_SHARE,
    customHeaderRender: () => <>{`${i18n.t('mention_other')} (${i18n.t('percentShare')})`}</>,
    customCellRender: getFormattedValue,
    cellProps: {
      width: (_, { constraints }) => constraints.columnWidth
    },
    headerProps: {
      textAlign: 'right'
    }
  },
  {
    field: TopicComparisonMeasureType.MENTIONS_TREND,
    customHeaderRender: () => <>{`${i18n.t('mention_other')} (${i18n.t('trend')})`}</>,
    customCellRender: (cellValue, { theme, constraints }) => {
      if (cellValue.length <= 1) {
        return MessageConst.EM_DASH
      }
      return (
        <SparklineContainer maxWidth={constraints.maxSparklineWidth}>
          <Sparkline mode={theme} data={cellValue} />
        </SparklineContainer>
      )
    },
    cellProps: {
      textAlign: 'left',
      width: (_, { constraints }) => constraints.columnWidth,
      tooltipContent: () => i18n.t('notAvailableTrend'),
      displayTooltip: (cellValue) => cellValue.length <= 1
    }
  },
  {
    field: TopicComparisonMeasureType.POSTS,
    customHeaderRender: () => <>{i18n.t('post_other')}</>,
    children: [
      {
        customCellRender: getFormattedValue,
        cellProps: {
          colSidePaddings: { right: '8px' },
          textAlign: 'right',
          fitWidth: true
        }
      },
      {
        customCellRender: (_, { row, theme }) => (
          <SimpleBar
            progress={row[TopicComparisonMeasureType.POSTS_PERCENT_SHARE].value * 100}
            colorSetting="metric.mainMetrics.post"
            mode={theme}
          />
        ),
        cellProps: {
          colSidePaddings: { left: '0px' },
          width: (_, { constraints }) => constraints.columnWidth
        }
      }
    ],
    customFooterRender: (context) => {
      const { statistics } = context
      return (
        <>
          <span>{statistics.totalPosts}</span>
          <span>({i18n.t('total')})</span>
        </>
      )
    }
  },
  {
    field: TopicComparisonMeasureType.POSTS_PERCENT_SHARE,
    customHeaderRender: () => <>{`${i18n.t('post_other')} (${i18n.t('percentShare')})`}</>,
    customCellRender: getFormattedValue,
    cellProps: {
      width: (_, { constraints }) => constraints.columnWidth
    },
    headerProps: {
      textAlign: 'right'
    }
  },
  {
    field: TopicComparisonMeasureType.POSTS_TREND,
    customHeaderRender: () => <>{`${i18n.t('post_other')} (${i18n.t('trend')})`}</>,
    customCellRender: (cellValue, { theme, constraints }) => {
      if (cellValue.length <= 1) {
        return MessageConst.EM_DASH
      }
      return (
        <SparklineContainer maxWidth={constraints.maxSparklineWidth}>
          <Sparkline mode={theme} data={cellValue} />
        </SparklineContainer>
      )
    },
    cellProps: {
      textAlign: 'left',
      width: (_, { constraints }) => constraints.columnWidth,
      tooltipContent: () => i18n.t('notAvailableTrend'),
      displayTooltip: (cellValue) => cellValue.length <= 1
    }
  },
  {
    field: TopicComparisonMeasureType.IMPRESSIONS,
    customHeaderRender: () => <>{i18n.t('potentialImpression')}</>,
    customCellRender: getFormattedValue,
    cellProps: {
      width: (_, { constraints }) => constraints.columnWidth
    },
    headerProps: {
      textAlign: 'right'
    }
  },
  {
    field: TopicComparisonMeasureType.UNIQUE_AUTHORS,
    customHeaderRender: () => <>{i18n.t('author_other')}</>,
    customCellRender: getFormattedValue,
    cellProps: {
      width: (_, { constraints }) => constraints.columnWidth
    },
    headerProps: {
      textAlign: 'right'
    }
  },
  {
    field: TopicComparisonMeasureType.NET_SENTIMENT,
    customHeaderRender: () => (
      <>
        <span>{i18n.t('netSentiment')}</span>
        <span> (Scale: -100% to 100%)</span>
      </>
    ),
    children: [
      {
        customCellRender: getFormattedValue,
        cellProps: {
          colSidePaddings: { right: '8px' },
          textAlign: 'right',
          fitWidth: true,
          tooltipContent: () => i18n.t('notAvailableNetSentiment'),
          displayTooltip: (cellValue) => getFormattedValue(cellValue) === MessageConst.EM_DASH
        }
      },
      {
        customCellRender: (cellValue, { theme, statistics }) => {
          let progress = 0
          let indicatorPosition
          if (cellValue.formattedValue !== MessageConst.EM_DASH) {
            // scale: -100 ~ 100
            progress = cellValue.value / 2 + 50
            indicatorPosition = statistics.averageSentiment.value / 2 + 50
          }
          return (
            <SimpleBar
              progress={progress}
              indicatorPosition={indicatorPosition}
              indicatorValue={statistics.averageSentiment.formattedValue}
              colorSetting="metric.mainMetrics.netSentiment"
              mode={theme}
            />
          )
        },
        cellProps: {
          colSidePaddings: { left: '0px' },
          width: (_, { constraints }) => constraints.columnWidth
        }
      }
    ],
    customFooterRender: (context) => {
      const { statistics } = context
      return (
        <>
          <span>{statistics.averageSentiment.formattedValue}</span>
          <span>({i18n.t('average')})</span>
        </>
      )
    }
  },
  {
    field: TopicComparisonMeasureType.PASSION,
    customHeaderRender: () => (
      <>
        <span>{i18n.t('passionIntensity')}</span>
        <span> (Scale: 0 to 100)</span>
      </>
    ),
    children: [
      {
        customCellRender: getFormattedValue,
        cellProps: {
          colSidePaddings: { right: '8px' },
          textAlign: 'right',
          fitWidth: true,
          tooltipContent: () => i18n.t('notAvailablePassionIntensity'),
          displayTooltip: (cellValue) => getFormattedValue(cellValue) === MessageConst.EM_DASH
        }
      },
      {
        customCellRender: (cellValue, { theme, statistics }) => {
          let progress = 0
          let indicatorPosition
          if (cellValue.formattedValue !== MessageConst.EM_DASH) {
            progress = cellValue.value
            indicatorPosition = statistics.averagePassion.value
          }
          return (
            <SimpleBar
              progress={progress}
              indicatorPosition={indicatorPosition}
              indicatorValue={statistics.averagePassion.formattedValue}
              colorSetting="metric.mainMetrics.passionIntensity"
              mode={theme}
            />
          )
        },
        cellProps: {
          colSidePaddings: { left: '0px' },
          width: (_, { constraints }) => constraints.columnWidth
        }
      }
    ],
    customFooterRender: (context) => {
      const { statistics } = context
      return (
        <>
          <span>{statistics.averagePassion.formattedValue}</span>
          <span>({i18n.t('average')})</span>
        </>
      )
    }
  },
  {
    field: TopicComparisonMeasureType.ENGAGEMENTS,
    customHeaderRender: () => <>{i18n.t('totalEngagement_other')}</>,
    customCellRender: getFormattedValue,
    cellProps: {
      width: (_, { constraints }) => constraints.columnWidth
    },
    headerProps: {
      textAlign: 'right'
    }
  },
  {
    field: TopicComparisonMeasureType.COMMENTS,
    customHeaderRender: () => <>{i18n.t('comment_other')}</>,
    customCellRender: getFormattedValue,
    cellProps: {
      width: (_, { constraints }) => constraints.columnWidth
    },
    headerProps: {
      textAlign: 'right'
    }
  },
  {
    field: TopicComparisonMeasureType.SHARES,
    customHeaderRender: () => <>{i18n.t('share_other')}</>,
    customCellRender: getFormattedValue,
    cellProps: {
      width: (_, { constraints }) => constraints.columnWidth
    },
    headerProps: {
      textAlign: 'right'
    }
  },
  {
    field: TopicComparisonMeasureType.LIKES,
    customHeaderRender: () => <>{i18n.t('like_other')}</>,
    customCellRender: getFormattedValue,
    cellProps: {
      width: (_, { constraints }) => constraints.columnWidth
    },
    headerProps: {
      textAlign: 'right'
    }
  },
  {
    field: TopicComparisonMeasureType.VIEWS,
    customHeaderRender: () => <>{i18n.t('view_other')}</>,
    customCellRender: getFormattedValue,
    cellProps: {
      width: (_, { constraints }) => constraints.columnWidth
    },
    headerProps: {
      textAlign: 'right'
    }
  },
  {
    field: TopicComparisonMeasureType.SCORE,
    customHeaderRender: () => <>{i18n.t('score_other')}</>,
    customCellRender: getFormattedValue,
    cellProps: {
      width: (_, { constraints }) => constraints.columnWidth
    },
    headerProps: {
      textAlign: 'right'
    }
  },
  {
    field: TopicComparisonMeasureType.ENGAGEMENT_RATIO_ONLY,
    customHeaderRender: () => <>{i18n.t('engagementRatio')}</>,
    customCellRender: getFormattedValue,
    cellProps: {
      width: (_, { constraints }) => constraints.columnWidth
    },
    headerProps: {
      textAlign: 'right'
    }
  }
]

export default colDefs
